import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import PageState from '../layouts/PageState';
import { useV2Common } from '../state/CommonProvider';
import { getCatalogCategories, getProducts } from '../services/commerce';

// ---------------------------------------------------------------------------
// Category chip bar
// ---------------------------------------------------------------------------

function CategoryBar({ categories, selected, onSelect }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, mt:1.5 }}>
      {categories.map((cat) => (
        <Chip
          key={cat.documentId || cat.id}
          label={cat.name}
          clickable
          color={selected === cat.name ? 'primary' : 'default'}
          variant={selected === cat.name ? 'filled' : 'outlined'}
          onClick={() => onSelect(cat.name)}
          sx={{ fontWeight: selected === cat.name ? 700 : 400 }}
        />
      ))}
    </Box>
  );
}

function CategorySidebar({ categories, selected, onSelect }) {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: { md: 72 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        maxHeight: { md: 'calc(100vh - 88px)' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" fontWeight={800} letterSpacing="-0.02em">
          Categories
        </Typography>
      </Box>
      <List dense disablePadding sx={{ overflow: 'auto', py: 0.5 }}>
        <ListItemButton
          selected={!selected}
          onClick={() => onSelect('')}
          sx={{ borderRadius: 1, mx: 0.5, mb: 0.25 }}
        >
          <ListItemText
            primary="All products"
            primaryTypographyProps={{ fontWeight: !selected ? 700 : 500, fontSize: '0.9rem' }}
          />
        </ListItemButton>
        {categories.map((cat) => {
          const sel = selected === cat.name;
          return (
            <ListItemButton
              key={cat.documentId || cat.id}
              selected={sel}
              onClick={() => onSelect(cat.name)}
              sx={{ borderRadius: 1, mx: 0.5, mb: 0.25 }}
            >
              <ListItemText
                primary={cat.name}
                primaryTypographyProps={{
                  fontWeight: sel ? 700 : 500,
                  fontSize: '0.9rem',
                  noWrap: true,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Product card
// ---------------------------------------------------------------------------

function ProductCard({ product, onClick }) {
  const lowestVariant = product.variants
    ?.filter((v) => v.visibleToCustomer !== false)
    .sort((a, b) => a.price - b.price)[0];

  const outOfStock = lowestVariant && lowestVariant.isInStock === false;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        opacity: outOfStock ? 0.55 : 1,
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': {
          borderColor: { md: 'primary.light' },
          boxShadow: { md: 3 },
        },
        '&:active': { bgcolor: 'action.hover' },
      }}
    >
      <CardMedia
        component="img"
        image={product.image || product.images?.[0] || noImage}
        alt={product.name}
        sx={{
          width: '100%',
          height: { md: 'auto' },
          aspectRatio: { md: '4 / 3' },
          maxHeight: { md: 220 },
          objectFit: 'cover',
          bgcolor: 'grey.100',
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = noImage;
          e.target.style.objectFit = 'contain';
          e.target.style.padding = '16px';
        }}
      />

      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {product.name}
          </Typography>
          {lowestVariant ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.25 }}>
              <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>
                ₹{lowestVariant.price}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lowestVariant.weight}
              </Typography>
            </Box>
          ) : null}
          {outOfStock && <Chip label="Out of stock" size="small" sx={{ mt: 0.5, fontSize: 10 }} />}
        </Box>

      </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Product grid skeleton
// ---------------------------------------------------------------------------

function ProductSkeleton() {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Skeleton variant="rectangular" width="100%" sx={{ aspectRatio: '4/3' }} />
            <Box sx={{ p: 1.5 }}>
              <Skeleton width="70%" height={24} />
              <Skeleton width="30%" height={28} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Catalog page
// ---------------------------------------------------------------------------

export default function Catalog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDesktop } = useV2Common();
  const selectedCategory = searchParams.get('category') || '';

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Load categories once
  useEffect(() => {
    getCatalogCategories()
      .then((res) => setCategories(res.data ?? []))
      .catch(() => {});
  }, []);

  const loadProducts = useCallback(
    async (category, page = 1, append = false) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const res = await getProducts({ category: category || undefined, page });
        if (append) {
          setProducts((prev) => [...prev, ...(res.data ?? [])]);
        } else {
          setProducts(res.data ?? []);
        }
        setPagination(res.pagination ?? null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Reload products when category changes
  useEffect(() => {
    loadProducts(selectedCategory, 1, false);
  }, [selectedCategory, loadProducts]);

  const handleCategorySelect = (catId) => {
    setSearchParams(catId ? { category: catId } : {});
  };

  const handleLoadMore = () => {
    if (!pagination) return;
    loadProducts(selectedCategory, pagination.page + 1, true);
  };

  const hasMore = pagination && pagination.page < pagination.pages;

  const productGrid = (
    <>
      {loading && <ProductSkeleton />}

      {!loading && error && (
        <PageState loading={false} error={error} onRetry={() => loadProducts(selectedCategory, 1, false)}>
          {null}
        </PageState>
      )}

      {!loading && !error && products.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography color="text.secondary">No products found</Typography>
        </Box>
      )}

      {!loading && !error && products.length > 0 && (
        <>
          <Grid container spacing={2.5}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <ProductCard
                  product={product}
                  onClick={() => navigate(`/v2/catalog/${product.id}`)}
                />
              </Grid>
            ))}
          </Grid>

          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              {loadingMore ? (
                <CircularProgress size={24} />
              ) : (
                <Chip label="Load more" variant="outlined" clickable onClick={handleLoadMore} />
              )}
            </Box>
          )}
        </>
      )}
    </>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 0 }, pt: { xs: 1, md: 2 }, pb: { xs: 10, md: 3 } }}>
      {isDesktop && categories.length > 0 ? (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item md={3}>
            <CategorySidebar
              categories={categories}
              selected={selectedCategory}
              onSelect={handleCategorySelect}
            />
          </Grid>
          <Grid item md={9}>
            {isDesktop && selectedCategory ? (
              <Typography variant="h6" fontWeight={800} sx={{ mb: 2, letterSpacing: '-0.02em' }}>
                {selectedCategory}
              </Typography>
            ) : isDesktop ? (
              <Typography variant="h6" fontWeight={800} sx={{ mb: 2, letterSpacing: '-0.02em' }}>
                All products
              </Typography>
            ) : null}
            {productGrid}
          </Grid>
        </Grid>
      ) : (
        <>
          {categories.length > 0 && (
            <CategoryBar
              categories={categories}
              selected={selectedCategory}
              onSelect={handleCategorySelect}
            />
          )}
          {productGrid}
        </>
      )}
    </Box>
  );
}
